import React from "react";
import { Helmet } from "react-helmet-async";
import { useInView } from "../hooks/useInView";

// Bio Section Component
const BioSection = ({ children, delay = 0 }) => {
  const [ref, isVisible] = useInView();
  return (
    <div 
      ref={ref}
      style={{
        opacity: 0,
        transform: 'translateX(-20px)',
        animation: isVisible ? `fadeInLeft 0.5s ease-in-out forwards ${delay}s` : 'none'
      }}
    >
      {children}
    </div>
  );
};

// Certificate Card Component
const CertificateCard = ({ title, institution, description, year, delay }) => {
  const [ref, isVisible] = useInView();
  return (
    <div 
      ref={ref}
      className="p-5 border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow bg-white"
      style={{
        opacity: 0,
        transform: 'scale(0.9)',
        animation: isVisible ? `scaleIn 0.5s ease-in-out forwards ${delay}s` : 'none'
      }}
    >
      <h3 className="text-xl font-semibold text-secondary mb-2">{title}</h3>
      {institution && <p className="text-gray-700">{institution}</p>}
      {description && <p className="text-sm text-gray-600 mt-1">{description}</p>}
      <p className="text-sm text-gray-500 mt-1">{year}</p>
    </div>
  );
};

// Profile Image Component
const ProfileImage = () => {
  const [ref, isVisible] = useInView();
  return (
    <div 
      ref={ref}
      className="w-full md:w-1/3 md:ml-8 sticky md:top-[120px] h-fit flex flex-col justify-start"
      style={{
        opacity: 0,
        transform: 'translateX(20px)',
        animation: isVisible ? 'fadeInRight 0.5s ease-in-out forwards' : 'none'
      }}
    >
      <img
        src={require("../assets/images/me.jpg")}
        alt="Psikolog Yağmur Demiröz – Bireysel Yetişkin Psikoterapisi, Ergen Psikoterapisi, Oyun Terapisi"
        className="rounded-md shadow-lg object-cover w-full h-auto"
        loading="lazy"
      />
    </div>
  );
};

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Hakkımda - Psikolog Yağmur Demiröz | Bireysel Yetişkin Psikoterapisi, Ergen Psikoterapisi, Oyun Terapisi</title>
        <meta
          name="description"
          content="Psikolog Yağmur Demiröz, Okan Üniversitesi Klinik Psikoloji Yüksek Lisans Tez Aşaması. Bireysel yetişkin psikoterapisi, ergen psikoterapisi, oyun terapisi, MOXO Testi ve psikolojik danışmanlık hizmetleri sunmaktadır."
        />
        <meta name="keywords" content="psikolog yağmur demiröz, klinik psikoloji, bireysel yetişkin psikoterapisi, ergen psikoterapisi, yetişkin terapisi, psikoterapi, oyun terapisi" />
        
        {/* Open Graph / Facebook / WhatsApp */}
        <meta property="og:type" content="profile" />
        <meta property="og:url" content="https://yagmurdemiroz.com/hakkimda" />
        <meta property="og:title" content="Hakkımda - Psikolog Yağmur Demiröz" />
        <meta property="og:description" content="Psikolog Yağmur Demiröz - Bireysel yetişkin psikoterapisi, ergen psikoterapisi, oyun terapisi ve psikolojik danışmanlık hizmetleri." />
        <meta property="og:image" content="https://yagmurdemiroz.com/me.jpg" />
        <meta property="og:locale" content="tr_TR" />
        <meta property="og:site_name" content="Yağmur Demiröz - Psikolog" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:url" content="https://yagmurdemiroz.com/hakkimda" />
        <meta name="twitter:title" content="Hakkımda - Psikolog Yağmur Demiröz" />
        <meta name="twitter:description" content="Psikolog Yağmur Demiröz - Bireysel yetişkin psikoterapisi, ergen psikoterapisi, oyun terapisi ve psikolojik danışmanlık hizmetleri." />
        <meta name="twitter:image" content="https://yagmurdemiroz.com/logo.png" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Person",
            "name": "Yağmur Demiröz",
            "jobTitle": "Psikolog",
            "worksFor": {
              "@type": "Organization",
              "name": "Özel Danışmanlık Merkezi"
            },
            "url": "https://yagmurdemiroz.com/hakkimda",
            "description": "Psikolog Yağmur Demiröz, bireysel yetişkin psikoterapisi, ergen psikoterapisi, oyun terapisi, MOXO Testi ve psikolojik danışmanlık hizmetleri sunmaktadır.",
            "alumniOf": {
              "@type": "CollegeOrUniversity",
              "name": "Maltepe Üniversitesi Psikoloji Bölümü"
            },
            "sameAs": [
              "https://www.instagram.com/psk.yagmurdemiroz",
              "https://www.linkedin.com/in/yagmurdemiroz"
            ]
          })}
        </script>
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        <header className="text-center mb-8">
          <div className="animate-fadeIn">
            <h1 className="text-4xl font-bold text-secondary">Psikolog Yağmur Demiröz</h1>
            <p className="text-lg text-gray-700 mt-2">
              Lisans Eğitimini Maltepe Üniversitesi'nde Tamamlamış | Okan Üniversitesi Klinik Psikoloji Yüksek Lisans Tez Aşaması
            </p>
          </div>
        </header>

        <div className="flex flex-col md:flex-row items-start">
          <div className="w-full md:w-2/3 mb-6 md:mb-0">
            <div className="space-y-4">
              <BioSection delay={0.1}>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Psikolog Yağmur Demiröz, lisans eğitimini tam burslu olarak kazandığı{" "}
                  <b>Maltepe Üniversitesi Psikoloji Bölümü</b>'nde {" "}
                  <b>yüksek onur derecesiyle</b> tamamlamıştır. Lisans eğitimi sürecinde aynı üniversitede Çift Anadal Programı kapsamında <b>İşletme bölümünü</b> de yüksek onur derecesiyle bitirmiştir. Şu anda <b>Okan Üniversitesi Tezli Klinik Psikoloji Yüksek Lisans Programı</b>'nda tez aşamasındadır. Şehir hastaneleri ve özel kliniklerde edindiği deneyimlerle klinik deneyimini pekiştirmiştir.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mt-4">
                  Şu anda <b>bireysel yetişkin psikoterapisi</b>, <b>ergen psikoterapisi</b>, <b>oyun terapisi</b> ve diğer destek türleri sunmaktadır.
                </p>
              </BioSection>

              <BioSection delay={0.2}>
                <div className="text-lg text-gray-700 leading-relaxed">
                  <p className="mb-2"><b>Mesleki Deneyimler:</b></p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Altınordu Üsküdar Futbol Okulu (2025-Halen)</li>
                    <li>Uzm. Dr. Psikiyatrist Serap Çakıcı'nın muayenehanesi (2023- Halen)</li>
                    <li>İstanbul Kartal Dr. Lütfi Kırdar Şehir Hastanesi (2023)</li>
                    <li>T.C. Küçükyalı Çocukevleri Sitesi (2022)</li>
                  </ul>
                </div>
              </BioSection>

              <BioSection delay={0.3}>
                <div className="text-lg text-gray-700 leading-relaxed">
                  <p className="mb-2"><b>Eğitim ve Sertifikalar:</b></p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Oyun Terapisi Eğitimi - Dr. Birgül Emiroğlu Bakay / Oyun Terapileri Derneği ve Amerika Oyun Terapileri Derneği (APT) onaylı</li>
                    <li>Moxo Dikkat Testi Uygulayıcı Sertifikası</li>
                    <li>İstanbul Üniversitesi - Öğrenci Koçluğu ve Eğitim Danışmanlığı</li>
                    <li>Objektif ve Projektif Çocuk Testleri Eğitimi</li>
                    <li>Mindfulness Temelli Stres Azaltma Programı (MBSR) Sertifikası</li>
                  </ul>
                </div>
              </BioSection>

              <p className="text-lg text-gray-700 leading-relaxed italic">
                Bireysel yetişkin psikoterapisi, Moxo test uygulaması ve diğer destek türleri için randevu almak üzere iletişim sayfasından bana ulaşabilirsiniz.
              </p>
            </div>

            <h2 
              className="text-2xl font-semibold text-secondary mt-6 mb-4"
              style={{
                animation: 'fadeInUp 0.5s ease-in-out forwards',
                opacity: 0,
                animationDelay: '0.5s'
              }}
            >
              Çalışma Alanlarım
            </h2>
            <ul className="list-disc list-inside text-lg text-gray-700 leading-relaxed mb-6">
              <li>Bireysel Yetişkin Psikoterapisi</li>
              <li>Ergen Psikoterapisi</li>
              <li>Moxo Dikkat Testi Uygulaması</li>
              <li>Öğrenci Danışmanlığı ve Koçluk</li>
              <li>Oyun Terapisi</li>
              <li>Çocuk Testleri Uygulaması</li>
              <li>Mindfulness Temelli Stres Azaltma (MBSR)</li>
            </ul>

            <section 
              className="mt-8"
              style={{
                animation: 'fadeInUp 0.5s ease-in-out forwards',
                opacity: 0,
                animationDelay: '0.3s'
              }}
            >
              <h2 className="text-2xl font-semibold text-secondary mb-6">Profesyonel Sertifikalarım</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <CertificateCard
                  title="Oyun Terapisi Sertifikası"
                  institution="Dr. Birgül Emiroğlu Bakay"
                  description="Oyun Terapileri Derneği ve APT (Association for Play Therapy) onaylı"
                  year="2024"
                  delay={0.4}
                />
                <CertificateCard
                  title="MOXO Dikkat Testi Uygulayıcı Sertifikası"
                  description="Resmi Uygulayıcı Sertifikası"
                  year="2024"
                  delay={0.5}
                />
                <CertificateCard
                  title="Mindfulness Temelli Stres Azaltma Programı (MBSR)"
                  description="Mindfulness ve Stres Yönetimi Sertifikası"
                  year="2024"
                  delay={0.6}
                />
                <CertificateCard
                  title="Öğrenci Koçluğu ve Eğitim Danışmanlığı"
                  institution="İstanbul Üniversitesi"
                  year="2022"
                  delay={0.7}
                />
                <CertificateCard
                  title="Objektif Projektif Çocuk Testleri"
                  description="Çocuk Değerlendirme Testleri Eğitimi"
                  year="2024"
                  delay={0.8}
                />
              </div>
            </section>
          </div>

          <ProfileImage />
        </div>
      </div>
    </div>
  );
};

export default AboutPage;